import { useState, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { useTranslation } from 'react-i18next'

import { motion, AnimatePresence } from 'framer-motion'

import { supabase } from '@/integrations/supabase/client'

import { useAuth } from '@/hooks/useAuth'
import SEO from '@/components/common/SEO'



const M = '#06060c'

const D = '#0a0a16'

const G = '#d4a843'

const IN = '#0ea5e9'

const EM = '#10b981'

const BR = 'rgba(255,255,255,0.07)'

const CA = 'rgba(255,255,255,0.04)'

const TX = 'rgba(255,255,255,0.88)'

const MU = 'rgba(255,255,255,0.45)'



// ─────────────────────────────────────────────────────────────────────────────

// PREP PROGRAMMES

//

// How the AI session system works:

//   PrepPage → navigate('/learn/:class_id')

//   → SessionPage (route: /learn/:classId)

//   → useUniversitySession(classId, moduleId, lessonId, ...)

//   → ai-education edge function

//   → looks up classes.category_id → conversation_flows.ai_instructions

//   → ai_instructions is the professor's system prompt (all the real teaching)

//

// class_id   = Master class row in `classes` table (has module_plan for navigation)

// category_id = links classes → conversation_flows (for AI professor instructions)

//

// The rich module/lesson content below is for DISPLAY on this page only.

// The AI professor uses conversation_flows.ai_instructions: not the module_plan

// titles in the DB (which are currently generic placeholders).

// ─────────────────────────────────────────────────────────────────────────────



interface Module { title: string; lessons: string[] }

interface PrepProgramme {

    slug: string

    class_id: string   // → /learn/:class_id → SessionPage → AI session

    label: string

    icon: string

    tier: 'free' | 'bridge'

    tagline: string

    overview: string

    modules: Module[]

    unlockCount: number

    prerequisite: string | null

    prereqLabel: string | null

    badge: string | null

}



// Program data is now generated dynamically within the component using translations.

function getProgrammes(t: any): PrepProgramme[] {

    const slugs = [

        'prep-critical-thinking', 'prep-study-skills', 'prep-career-exploration', 'prep-personal-finance',

        'prep-philosophy-ethics', 'prep-psychology', 'prep-sociology', 'prep-statistics', 'prep-entrepreneurship', 'prep-calculus'

    ]

    return slugs.map(slug => {

        const base = `university.prep.programmes.${slug}`

        const modules = [1, 2, 3, 4].map(m => {

            const mBase = `${base}.modules.m${m}`

            return {

                title: t(`${mBase}.title`),

                lessons: [1, 2, 3, 4].map(l => t(`${mBase}.l${l}`)).filter(l => l && l !== `${mBase}.l${l}`)

            }

        })



        // Determine tier based on slug

        const tier = slug.startsWith('prep-critical') || slug.startsWith('prep-study') || slug.startsWith('prep-career') || slug.startsWith('prep-personal') ? 'free' : 'bridge'



        // Hardcoded IDs and icons for now - logic stays same, labels are translated

        const meta: Record<string, { id: string, icon: string, unlock: number, prereq: string | null, prereqLabelPath: string | null, badge: string | null }> = {

            'prep-critical-thinking': { id: '72b58fdd-0847-4263-8984-77cb72b1fc95', icon: '🔍', unlock: 4, prereq: null, prereqLabelPath: null, badge: 'START HERE' },

            'prep-study-skills': { id: 'c2d365e1-cd5d-4417-9986-182dcc089618', icon: '📚', unlock: 2, prereq: null, prereqLabelPath: null, badge: 'FREE FOREVER' },

            'prep-career-exploration': { id: '7bb810fa-0c92-424a-91c0-7a6a048effad', icon: '🎯', unlock: 3, prereq: null, prereqLabelPath: null, badge: 'FREE FOREVER' },

            'prep-personal-finance': { id: '82e428c4-f80c-4474-a930-5e3eff6ccb59', icon: '💳', unlock: 2, prereq: null, prereqLabelPath: null, badge: 'FREE FOREVER' },

            'prep-philosophy-ethics': { id: '792af34b-064b-4760-b0cd-e0b4270315e7', icon: '🤔', unlock: 2, prereq: 'prep-critical-thinking', prereqLabelPath: 'university.prep.programmes.prep-critical-thinking.label', badge: null },

            'prep-psychology': { id: 'c9045fe0-5070-481f-a6dd-5c154e414124', icon: '🧠', unlock: 2, prereq: 'prep-critical-thinking', prereqLabelPath: 'university.prep.programmes.prep-critical-thinking.label', badge: null },

            'prep-sociology': { id: '1fc54151-bcdd-413e-980c-3fc182ceff79', icon: '👥', unlock: 3, prereq: 'prep-critical-thinking', prereqLabelPath: 'university.prep.programmes.prep-critical-thinking.label', badge: null },

            'prep-statistics': { id: '1413c9e2-15be-41c6-bc89-a9378aab2798', icon: '📊', unlock: 3, prereq: 'prep-study-skills', prereqLabelPath: 'university.prep.programmes.prep-study-skills.label', badge: 'HIGH DEMAND' },

            'prep-entrepreneurship': { id: 'e4d7dd24-c6f4-48c2-ba02-16e22aea3c7a', icon: '🚀', unlock: 2, prereq: 'prep-personal-finance', prereqLabelPath: 'university.prep.programmes.prep-personal-finance.label', badge: null },

            'prep-calculus': { id: '56caf32d-c584-447e-8e67-04c52ae6076d', icon: '∫', unlock: 3, prereq: 'prep-statistics', prereqLabelPath: 'university.prep.programmes.prep-statistics.label', badge: null },

        }



        return {

            slug,

            class_id: meta[slug].id,

            label: t(`${base}.label`),

            icon: meta[slug].icon,

            tier: tier as 'free' | 'bridge',

            tagline: t(`${base}.tagline`),

            overview: t(`${base}.overview`),

            modules,

            unlockCount: meta[slug].unlock,

            prerequisite: meta[slug].prereq,

            prereqLabel: meta[slug].prereqLabelPath ? t(meta[slug].prereqLabelPath) : null,

            badge: meta[slug].badge ? t(`university.prep.badge.${meta[slug].badge}`, meta[slug].badge) : null

        }

    })

}



// Programme variables are now managed within the PrepPage component using getProgrammes(t)





// ─── Card component ──────────────────────────────────────────────────────────

function ProgrammeCard({ prog, expanded, onToggle, completed, enrolled, prereqSatisfied, isMobile, optedOut, hasSubmission }: {
    prog: PrepProgramme; expanded: boolean; onToggle: () => void
    completed: boolean; enrolled: boolean; prereqSatisfied: boolean; isMobile: boolean
    optedOut: boolean; hasSubmission: boolean
}) {

    const { t } = useTranslation()

    const navigate = useNavigate()

    const { user, userProfile } = useAuth()

    const locked = !prereqSatisfied

    const isFoundation = prog.tier === 'free'
    const accent = isFoundation ? EM : G
    const [optOutSelected, setOptOutSelected] = useState(false)
    const [loading, setLoading] = useState(false)

    // Hide opt-out for special users (Corporate, NFP, Scholar)
    const isSpecialUser = !!(userProfile?.facility_id || userProfile?.nfp_pass_id || userProfile?.scholar_pass_id)
    const showOptOut = prog.tier === 'bridge' && !isSpecialUser && !optedOut && !completed



    async function handleStart() {
        if (locked || loading) return

        if (!user) {
            const next = encodeURIComponent(`/prep/start/${prog.slug}`)
            navigate(`/signin?next=${next}`)
            return
        }

        setLoading(true)



        // Bridge subjects require an active subscription

        if (prog.tier === 'bridge') {
            const isSubscribed = userProfile?.subscription_status === 'premium' || userProfile?.university_access === true
            if (!isSubscribed) {
                navigate('/enrol')
                setLoading(false)
                return
            }

            if (optOutSelected && !optedOut && !hasSubmission) {
                try {
                    const { data: { session: authSession } } = await supabase.auth.getSession()
                    const res = await supabase.functions.invoke('billing', {
                        body: {
                            action: 'essay_opt_out',
                            prep_slug: prog.slug
                        },
                        headers: {
                            Authorization: `Bearer ${authSession?.access_token}`
                        }
                    })
                    if (res.data?.url) {
                        window.location.href = res.data.url
                        return
                    }
                } catch (err) {
                    console.error('Opt-out checkout error:', err)
                }
            }
        }



        // Record prep enrolment

        await supabase.from('prep_enrolments').upsert({
            user_id: user.id,
            prep_slug: prog.slug,
            status: 'in_progress',
        }, { onConflict: 'user_id,prep_slug' })

        navigate(`/learn/${prog.class_id}`)
        setLoading(false)
    }



    return (

        <motion.div layout

            style={{

                background: expanded ? (isFoundation ? 'rgba(16,185,129,0.05)' : 'rgba(212,168,67,0.05)') : CA,

                border: `${expanded ? 2 : 1}px solid ${expanded ? accent : BR}`,

                borderRadius: 20, overflow: 'hidden', opacity: locked ? 0.6 : 1,

                transition: 'border-color 0.2s, background 0.2s, opacity 0.2s',

            }}

        >

            {/* Header row */}

            <div onClick={onToggle} style={{ padding: isMobile ? '18px 16px' : '22px 24px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 16 }}>

                <div style={{ width: 44, height: 44, borderRadius: 12, background: isFoundation ? 'rgba(16,185,129,0.1)' : 'rgba(212,168,67,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>

                    {locked ? '🔒' : prog.icon}

                </div>

                <div style={{ flex: 1, minWidth: 0 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>

                        <span style={{ fontWeight: 800, fontSize: isMobile ? 14 : 16, color: TX }}>{prog.label}</span>

                        {prog.badge && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '1px', background: isFoundation ? 'rgba(16,185,129,0.15)' : 'rgba(212,168,67,0.15)', color: accent, borderRadius: 100, padding: '2px 8px' }}>{prog.badge}</span>}

                        {completed && <span style={{ fontSize: 10, fontWeight: 800, background: 'rgba(16,185,129,0.15)', color: EM, borderRadius: 100, padding: '2px 8px' }}>{t('university.prep.statusComplete', 'COMPLETED')}</span>}

                        {enrolled && !completed && <span style={{ fontSize: 10, fontWeight: 800, background: 'rgba(14,165,233,0.15)', color: IN, borderRadius: 100, padding: '2px 8px' }}>{t('university.prep.statusInProgress', 'IN PROGRESS')}</span>}

                    </div>

                    <p style={{ fontSize: 13, color: MU, margin: '0 0 8px', lineHeight: 1.4 }}>{prog.tagline}</p>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

                        {prog.prerequisite && (

                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: prereqSatisfied ? 'rgba(16,185,129,0.1)' : 'rgba(248,113,113,0.1)', border: `1px solid ${prereqSatisfied ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)'}`, borderRadius: 100, padding: '3px 10px 3px 6px' }}>

                                <span style={{ fontSize: 11 }}>{prereqSatisfied ? '✓' : '🔒'}</span>

                                <span style={{ fontSize: 11, fontWeight: 700, color: prereqSatisfied ? EM : '#f87171' }}>{t('university.prep.lockedBtn', 'Requires')}: {prog.prereqLabel}</span>

                            </div>

                        )}

                        <span style={{ fontSize: 11, color: MU }}>{prog.modules.length} {t('university.enrol.modules', 'modules')} · {prog.modules.reduce((s, m) => s + m.lessons.length, 0)} {t('university.enrol.lessons', 'lessons')}</span>

                        {prog.unlockCount > 0 && <span style={{ fontSize: 11, color: accent, fontWeight: 700 }}>🔓 {t('university.enrol.unlocks', 'Unlocks')} {prog.unlockCount} {t('university.enrol.degrees', 'degrees')}</span>}

                    </div>

                </div>

                <div style={{ flexShrink: 0, textAlign: 'right' }}>

                    <div style={{ fontSize: 14, fontWeight: 900, color: accent, marginBottom: 2 }}>{t('university.prep.costLabel', 'FREE')}</div>

                    <div style={{ fontSize: 12, color: MU }}>{expanded ? '▲' : '▼'}</div>

                </div>

            </div>



            {/* Expanded body */}

            <AnimatePresence>

                {expanded && (

                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>

                        <div style={{ padding: isMobile ? '0 16px 20px' : '0 24px 24px', borderTop: `1px solid ${BR}` }}>



                            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: '20px 0' }}>{prog.overview}</p>



                            {locked && (

                                <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>

                                    <p style={{ fontSize: 13, color: '#f87171', margin: 0, fontWeight: 600 }}>🔒 {t('university.prep.lockedHint', 'Complete {{label}} first to unlock this programme.', { label: prog.prereqLabel })}</p>

                                </div>

                            )}



                            {/* Modules - from static data above (real content, not DB placeholders) */}

                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>

                                {prog.modules.map((mod, mi) => (

                                    <div key={mi} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, padding: '14px 16px' }}>

                                        <p style={{ fontSize: 11, fontWeight: 800, color: accent, margin: '0 0 10px', letterSpacing: '0.5px' }}>

                                            {t('university.enrol.module', 'MODULE')} {mi + 1} · {mod.title.toUpperCase()}

                                        </p>

                                        {mod.lessons.map((lesson, li) => (

                                            <div key={li} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>

                                                <span style={{ color: accent, fontSize: 10, fontWeight: 900, flexShrink: 0, marginTop: 3 }}>›</span>

                                                <span style={{ fontSize: 12, color: MU, lineHeight: 1.45 }}>{lesson}</span>

                                            </div>

                                        ))}

                                    </div>

                                ))}

                            </div>



                            {/* Essay Opt-Out Toggle */}
                            {showOptOut && (
                                <div style={{ background: 'rgba(212,168,67,0.06)', border: `1px solid ${BR}`, borderRadius: 12, padding: '16px', marginBottom: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <input
                                            type="checkbox"
                                            id={`optout-${prog.slug}`}
                                            checked={optOutSelected}
                                            onChange={(e) => setOptOutSelected(e.target.checked)}
                                            style={{ width: 18, height: 18, cursor: 'pointer', accentColor: G }}
                                        />
                                        <label htmlFor={`optout-${prog.slug}`} style={{ fontSize: 13, fontWeight: 700, color: G, cursor: 'pointer' }}>
                                            Opt-out of Essay Assessments ($10 one-time fee)
                                        </label>
                                    </div>
                                    <p style={{ fontSize: 12, color: MU, margin: '8px 0 0 30px', lineHeight: 1.5 }}>
                                        By opting out, you will only need to complete AI short-answer checks. You will receive a
                                        <strong> Certificate of Completion (Essay-Free)</strong> instead of the standard Academic Certificate.
                                    </p>
                                    {hasSubmission && (
                                        <p style={{ fontSize: 11, color: '#f87171', margin: '4px 0 0 30px', fontWeight: 600 }}>
                                            Note: Opt-out is not available as you have already begun submitting assessments.
                                        </p>
                                    )}
                                </div>
                            )}

                            {optedOut && (
                                <div style={{ background: 'rgba(14,165,233,0.06)', border: `1px solid rgba(14,165,233,0.3)`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <span style={{ fontSize: 16 }}>⚡</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: IN }}>
                                        Essay Opt-Out Active: You are on the fast-track (Short-answer only).
                                    </span>
                                </div>
                            )}

                            {/* Unlocks callout */}
                            {prog.unlockCount > 0 && (
                                <div style={{ background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
                                    <p style={{ fontSize: 12, fontWeight: 700, color: '#7dd3fc', margin: '0 0 4px' }}>🎓 {t('university.enrol.unlocks', 'Completing this unlocks')}:</p>
                                    <p style={{ fontSize: 12, color: MU, margin: 0 }}>{prog.unlockCount} {t('university.enrol.degrees', 'degree programmes')} in the main colleges</p>
                                </div>
                            )}



                            {/* CTA */}

                            <motion.button

                                whileHover={{ scale: locked ? 1 : 1.02 }}

                                whileTap={{ scale: locked ? 1 : 0.97 }}

                                onClick={handleStart}

                                disabled={locked}

                                style={{

                                    width: '100%',

                                    background: locked

                                        ? 'rgba(255,255,255,0.05)'

                                        : completed

                                            ? `linear-gradient(135deg, ${EM}, #059669)`

                                            : isFoundation

                                                ? `linear-gradient(135deg, ${EM}, #059669)`

                                                : `linear-gradient(135deg, ${G}, #b8912e)`,

                                    color: locked ? MU : isFoundation ? '#fff' : '#06060c',

                                    border: 'none', borderRadius: 12, padding: '14px',

                                    fontSize: 14, fontWeight: 800,

                                    cursor: locked ? 'not-allowed' : 'pointer',

                                    boxShadow: locked ? 'none' : `0 4px 20px ${isFoundation ? 'rgba(16,185,129,0.3)' : 'rgba(212,168,67,0.3)'}`,

                                }}

                            >

                                {loading
                                    ? t('common.loading', 'Loading...')
                                    : locked
                                        ? `🔒 ${t('university.prep.lockedBtn', 'Complete {{label}} first', { label: prog.prereqLabel })}`
                                        : completed ? `✓ ${t('university.prep.reviewBtn', 'Review this Programme')}`
                                            : enrolled ? t('university.prep.continueBtn', '▶ Continue')
                                                : optOutSelected
                                                    ? `Opt-out & Start: ${prog.label} ($10) →`
                                                    : `${t('university.prep.startBtn', 'Start')}: ${prog.label} →`}
                            </motion.button>



                        </div>

                    </motion.div>

                )}

            </AnimatePresence>

        </motion.div>

    )

}



// ─── Main page ────────────────────────────────────────────────────────────────

export default function PrepPage() {

    const { t } = useTranslation()

    const navigate = useNavigate()

    const { user, userProfile } = useAuth()
    const mode = userProfile?.prep_pathway_mode || 'guided'



    const PREP_PROGRAMMES = getProgrammes(t)

    const FOUNDATIONS = mode === 'bridge_only'
        ? PREP_PROGRAMMES.filter(p => p.tier === 'free' && enrolledSlugs.has(p.slug))
        : PREP_PROGRAMMES.filter(p => p.tier === 'free')

    const BRIDGES = PREP_PROGRAMMES.filter(p => p.tier === 'bridge')



    const [isMobile, setIsMobile] = useState(false)

    const [expanded, setExpanded] = useState<string | null>('prep-critical-thinking')

    const [completedSlugs, setCompleted] = useState<Set<string>>(new Set())
    const [enrolledSlugs, setEnrolled] = useState<Set<string>>(new Set())
    const [optedOutSlugs, setOptedOut] = useState<Set<string>>(new Set())
    const [slugsWithSubmissions, setSubmissions] = useState<Set<string>>(new Set())



    const { slug } = useParams<{ slug: string }>()

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 1024)
        check(); window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    // Handle auto-start from URL /prep/start/:slug
    useEffect(() => {
        if (!user || !slug) return
        const prog = PREP_PROGRAMMES.find(p => p.slug === slug)
        if (!prog) return

        // If they landed here via /prep/start/:slug, we should auto-enroll and redirect to /learn/:class_id
        // but only if it's available (not locked)
        const lockedFoundations = mode === 'guided' && prog.prerequisite && !completedSlugs.has(prog.prerequisite)
        if (lockedFoundations) return

        const start = async () => {
            // Record enrolment
            await supabase.from('prep_enrolments').upsert({
                user_id: user.id,
                prep_slug: prog.slug,
                status: 'in_progress',
            }, { onConflict: 'user_id,prep_slug' })

            // Redirect to the chat
            navigate(`/learn/${prog.class_id}`, { replace: true })
        }

        start()
    }, [user, slug, completedSlugs, navigate, PREP_PROGRAMMES])



    useEffect(() => {
        if (!user) return

        const fetchEnrolments = async () => {
            const { data } = await supabase
                .from('prep_enrolments')
                .select('prep_slug, status, essay_opted_out')
                .eq('user_id', user.id)

            const done = new Set<string>()
            const inProg = new Set<string>()
            const opted = new Set<string>()

                ; (data || []).forEach(e => {
                    if (e.status === 'completed') done.add(e.prep_slug)
                    else inProg.add(e.prep_slug)
                    if (e.essay_opted_out) opted.add(e.prep_slug)
                })

            setCompleted(done)
            setEnrolled(inProg)
            setOptedOut(opted)

            // Also fetch if any submissions exist to prevent opt-out
            // We look at university_submissions for the user
            const { data: subs } = await supabase
                .from('university_submissions')
                .select('class_id')
                .eq('student_id', user.id)

            if (subs) {
                const subClassIds = new Set(subs.map(s => s.class_id))
                const activePrepWithSubs = new Set<string>()
                PREP_PROGRAMMES.forEach(p => {
                    if (subClassIds.has(p.class_id)) activePrepWithSubs.add(p.slug)
                })
                setSubmissions(activePrepWithSubs)
            }
        }

        fetchEnrolments()
    }, [user])



    function isUnlocked(prog: PrepProgramme) {
        if (!prog.prerequisite) return true
        if (mode === 'bridge_only' || mode === 'direct') return true
        return completedSlugs.has(prog.prerequisite)
    }



    const completedCount = PREP_PROGRAMMES.filter(p => completedSlugs.has(p.slug)).length



    return (

        <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
            <SEO
                title="University Prep"
                description="Start your university journey with 10 free preparatory subjects. Learn critical thinking, academic research, and study strategies from our AI professor."
            />



            {/* ── Hero ─────────────────────────────────────────────────────────────── */}

            <div style={{ background: `linear-gradient(170deg, #07071a 0%, ${D} 60%, ${M} 100%)`, borderBottom: `1px solid ${BR}`, position: 'relative', overflow: 'hidden' }}>

                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(14,165,233,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

                <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '44px 20px 40px' : '64px 24px 56px', textAlign: 'center', position: 'relative' }}>

                    <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>

                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 100, padding: '6px 16px', marginBottom: 20 }}>

                            <span style={{ fontSize: 14 }}>🎓</span>

                            <span style={{ fontSize: 11, fontWeight: 800, color: EM, letterSpacing: '2px' }}>{t('university.prep.eyebrow', 'COLLEGE 11 · UNIVERSITY PREPARATION')}</span>

                        </div>

                        <h1 style={{ fontSize: isMobile ? 28 : 44, fontWeight: 900, margin: '0 0 16px', letterSpacing: '-1px', fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.1 }}>

                            {t('university.prep.title', 'Start Here. All Free.')}

                        </h1>

                        <p style={{ color: MU, fontSize: isMobile ? 14 : 17, lineHeight: 1.75, maxWidth: 580, margin: '0 auto 28px' }}>

                            {t('university.prep.sub', 'Ten prep subjects, all completely free. Foundation subjects have no prerequisites: just start. Bridge subjects require a foundation first, by academic design, not payment gates.')}

                        </p>

                        <div style={{ display: 'flex', gap: isMobile ? 12 : 32, background: CA, border: `1px solid ${BR}`, borderRadius: isMobile ? 24 : 100, padding: isMobile ? '16px' : '10px 24px', flexWrap: 'wrap', justifyContent: 'center' }}>

                            {[

                                { value: '4', label: t('university.prep.stats.foundations', 'Foundation subjects'), color: EM },

                                { value: '6', label: t('university.prep.stats.bridges', 'Bridge subjects'), color: G },

                                { value: '45', label: t('university.prep.stats.degrees', 'Degrees unlocked'), color: IN },

                                { value: '$0', label: t('university.prep.stats.cost', 'to start'), color: EM },

                            ].map(s => (

                                <div key={s.label} style={{ textAlign: 'center' }}>

                                    <div style={{ fontSize: 20, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>

                                    <div style={{ fontSize: 10, color: MU, fontWeight: 600 }}>{s.label}</div>

                                </div>

                            ))}

                        </div>

                    </motion.div>

                </div>

            </div>



            {/* ── Progress strip ───────────────────────────────────────────────────── */}

            {user && completedCount > 0 && (

                <div style={{ background: 'rgba(16,185,129,0.05)', borderBottom: '1px solid rgba(16,185,129,0.15)' }}>

                    <div style={{ maxWidth: 900, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>

                        <span style={{ fontSize: 16 }}>🌱</span>

                        <span style={{ fontSize: 13, color: TX, fontWeight: 600 }}>{t('university.prep.progress.text', "You've completed {{count}} of {{total}} prep programmes", { count: completedCount, total: PREP_PROGRAMMES.length })}</span>

                        {completedCount >= 2 && <span style={{ fontSize: 12, color: MU }}>· {t('university.prep.progress.ready', "You're ready to start your first degree")}</span>}

                        <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate('/enrol')}

                            style={{ marginLeft: 'auto', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 10, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>

                            {t('university.prep.progress.browseBtn', 'Browse Degrees →')}

                        </motion.button>

                    </div>

                </div>

            )}



            <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '52px 24px 100px' }}>



                {/* Commitment */}

                <div style={{ background: 'rgba(212,168,67,0.04)', border: '1px solid rgba(212,168,67,0.15)', borderRadius: 16, padding: '18px 24px', marginBottom: 40, display: 'flex', gap: 14, alignItems: 'flex-start' }}>

                    <span style={{ fontSize: 20, flexShrink: 0 }}>⚖️</span>

                    <div>

                        <p style={{ fontSize: 13, fontWeight: 700, color: G, margin: '0 0 4px' }}>{t('university.prep.commitmentTitle', 'Our Commitment to Truth Over Bias')}</p>

                        <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.6 }}>{t('university.prep.commitmentSub', 'Still Waters University presents facts as established through the scientific method, and labels theories as theories. We do not lean left or right. Our goal is to equip you to think critically - not to tell you what to think.')}</p>

                    </div>

                </div>



                {/* ── Foundation subjects ─────────────────────────────────────────────── */}

                <section style={{ marginBottom: 52 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>

                        <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, rgba(16,185,129,0.4), transparent)' }} />

                        <span style={{ fontSize: 11, fontWeight: 800, color: EM, letterSpacing: '2px', whiteSpace: 'nowrap' }}>{t('university.prep.foundations.title', 'PART ONE · FOUNDATION SUBJECTS · FREE')}</span>

                        <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.4))' }} />

                    </div>

                    <p style={{ fontSize: 13, color: MU, textAlign: 'center', margin: '0 0 28px', lineHeight: 1.6 }}>

                        {t('university.prep.foundations.sub', 'These four subjects are permanently free. No card. No account required: just start. Progress saves automatically when you create an account.')}

                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                        {FOUNDATIONS.map(prog => (
                            <ProgrammeCard key={prog.slug} prog={prog}
                                expanded={expanded === prog.slug}
                                onToggle={() => setExpanded(expanded === prog.slug ? null : prog.slug)}
                                completed={completedSlugs.has(prog.slug)}
                                enrolled={enrolledSlugs.has(prog.slug)}
                                optedOut={optedOutSlugs.has(prog.slug)}
                                hasSubmission={slugsWithSubmissions.has(prog.slug)}
                                prereqSatisfied={true}
                                isMobile={isMobile}
                            />
                        ))}

                    </div>

                </section>



                {/* ── Bridge subjects ─────────────────────────────────────────────────── */}

                <section style={{ marginBottom: 52 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>

                        <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, rgba(212,168,67,0.4), transparent)' }} />

                        <span style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: '2px', whiteSpace: 'nowrap' }}>{t('university.prep.bridges.title', 'PART TWO · BRIDGE SUBJECTS · FREE (REQUIRES FOUNDATION)')}</span>

                        <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.4))' }} />

                    </div>

                    <p style={{ fontSize: 13, color: MU, textAlign: 'center', margin: '0 0 28px', lineHeight: 1.6 }}>

                        {t('university.prep.bridges.sub', 'These unlock specific degree programmes. Each requires a foundation subject first: academic gates, not payment gates.')}

                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                        {BRIDGES.map(prog => (

                            <ProgrammeCard
                                key={prog.slug} prog={prog}
                                expanded={expanded === prog.slug}
                                onToggle={() => setExpanded(expanded === prog.slug ? null : prog.slug)}
                                completed={completedSlugs.has(prog.slug)}
                                enrolled={enrolledSlugs.has(prog.slug)}
                                optedOut={optedOutSlugs.has(prog.slug)}
                                hasSubmission={slugsWithSubmissions.has(prog.slug)}
                                prereqSatisfied={isUnlocked(prog)}
                                isMobile={isMobile}
                            />

                        ))}

                    </div>

                </section>



                {/* ── How prereqs work ────────────────────────────────────────────────── */}

                <div style={{ background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 20, padding: isMobile ? '24px 20px' : '32px 36px', marginBottom: 52 }}>

                    <p style={{ fontSize: 11, fontWeight: 800, color: '#7dd3fc', letterSpacing: '2px', marginBottom: 20, textAlign: 'center' }}>{t('university.prep.prereqs.title', 'HOW PREREQUISITES WORK')}</p>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20 }}>

                        {[

                            { icon: '🟢', title: t('university.prep.prereqs.step1Title', 'Free forever'), desc: t('university.prep.prereqs.step1Desc', 'Critical Thinking, Study Skills, Career Exploration, Personal Finance. No account needed to start.') },

                            { icon: '🔓', title: t('university.prep.prereqs.step2Title', 'Foundation → Bridge'), desc: t('university.prep.prereqs.step2Desc', 'Completing Critical Thinking unlocks Philosophy, Psychology, and Sociology bridges.') },

                            { icon: '🎓', title: t('university.prep.prereqs.step3Title', 'Bridge → Degree'), desc: t('university.prep.prereqs.step3Desc', 'Completing a bridge unlocks the corresponding degree in the main colleges. Academic gates, not payment gates.') },

                        ].map(item => (

                            <div key={item.title} style={{ textAlign: 'center' }}>

                                <div style={{ fontSize: 28, marginBottom: 12 }}>{item.icon}</div>

                                <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 8px', color: TX }}>{item.title}</p>

                                <p style={{ fontSize: 13, color: MU, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>

                            </div>

                        ))}

                    </div>

                </div>



                {/* ── Bottom CTAs ─────────────────────────────────────────────────────── */}

                <div style={{ textAlign: 'center' }}>

                    <p style={{ fontSize: 15, color: MU, marginBottom: 20 }}>{t('university.prep.ctaTitle', 'Ready to jump straight into the full degrees?')}</p>

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>

                        <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate('/enrol')}

                            style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>

                            {t('university.prep.ctaBrowseBtn', 'Browse All 45 Degrees →')}

                        </motion.button>

                        <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate('/give')}

                            style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)', color: '#7dd3fc', borderRadius: 12, padding: '14px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>

                            {t('university.prep.ctaGiveBtn', '💙 Pay It Forward')}

                        </motion.button>

                    </div>

                </div>



            </div>

        </div>

    )

}